'use client';

import {
  createContext,
  useContext,
  useState,
  PropsWithChildren,
  useMemo,
} from 'react';
import {
  AssetHistoryPeriod,
  AssetHistoryViewQuery$data,
  type AssetDetailsViewQuery$data,
} from '@bts-web/data-layer/server';
import { TimeAmounts } from '@bts-web/design-system/component/chart';
import { useTradeSellDisabler } from '../../../trade/TradeDrawer/useTradeSellDisabler/useTradeSellDisabler';
import { PriceChangeTimeAmountKeys } from '../../utils/PriceChangeTimeAmountKeys';
import { PrecisionFloat } from '@bts-web/utils-formatting';
import { getAssetHistoricalInterval } from '../../../common/gqlActions/getAssetHistoricalInterval.server';

export type AssetFields = AssetDetailsViewQuery$data['assetById'];

export type TrendType = 'positive' | 'negative' | 'neutral';

type ValueChange = {
  value: string;
  percentage: string;
};

interface AssetDetailsDataContextValue {
  assetData: NonNullable<AssetDetailsViewQuery$data['assetById']>;
  assetHistory: NonNullable<
    NonNullable<AssetHistoryViewQuery$data['assetById']>
  >['history'];
  loading: boolean;
  timeRange: TimeAmounts;
  changeTimeRange: (newPeriod: TimeAmounts) => void;
  trend: TrendType;
  priceChange?: ValueChange | null;
}

const PeriodsIds: Record<TimeAmounts, AssetHistoryPeriod> = {
  [TimeAmounts.OneDay]: 'DAY',
  [TimeAmounts.SevenDays]: 'WEEK',
  [TimeAmounts.OneMonth]: 'MONTH',
  [TimeAmounts.OneYear]: 'YEAR',
  [TimeAmounts.SixMonths]: 'SIX_MONTHS',
  [TimeAmounts.MaxYears]: 'FIVE_YEARS',
};

export const AssetDetailsDataContext =
  createContext<AssetDetailsDataContextValue>(
    {} as AssetDetailsDataContextValue,
  );

export function useAssetDetailsDataContext() {
  const contextValue = useContext(AssetDetailsDataContext);

  if (contextValue === null) {
    throw new Error(
      'useAssetDetailsDataContext must be used within a AssetDetailsDataContextProvider',
    );
  }

  return contextValue;
}

const ASSET_DETAILS_SELL_BUTTON_KEY = 'ASSET_DETAILS_SELL_BUTTON_KEY';

export const AssetDetailsDataContextProvider = ({
  children,
  initialData,
}: PropsWithChildren<{
  initialData: NonNullable<AssetDetailsViewQuery$data['assetById']>;
}>) => {
  const [assetData] =
    useState<AssetDetailsDataContextValue['assetData']>(initialData);

  const [assetHistory, setAssetHistory] = useState<
    AssetDetailsDataContextValue['assetHistory']
  >(initialData.history);

  const [loading, setLoading] = useState(false);

  const [timeRange, setTimeRange] = useState<TimeAmounts>(TimeAmounts.OneDay);

  const changeTimeRange = async (newPeriod: TimeAmounts) => {
    setLoading(true);

    const periodQueryVarFormat = PeriodsIds[newPeriod] as AssetHistoryPeriod;

    try {
      const response = await getAssetHistoricalInterval({
        assetHistoryPeriod: periodQueryVarFormat,
        assetId: initialData.id,
      });

      const newAssetHistoryData = response.data?.assetById?.history;

      if (!newAssetHistoryData) {
        return;
      }

      setTimeRange(newPeriod);

      setAssetHistory(newAssetHistoryData);
    } catch (error) {
      // Handle error appropriately (e.g., log, show error message)
      console.error('Failed to fetch asset history:', error);
    } finally {
      setLoading(false);
    }
  };

  const isAssetOwned =
    parseFloat(
      (assetData.portfolio?.assetBalance as NonNullable<PrecisionFloat>).value,
    ) > 0;

  useTradeSellDisabler({
    shouldDisable: isAssetOwned === false,
    disablerKey: ASSET_DETAILS_SELL_BUTTON_KEY,
  });

  const { priceChange, trend } = useMemo(() => {
    const priceTimekey = PriceChangeTimeAmountKeys[timeRange];

    const priceChange: ValueChange | null | undefined =
      priceTimekey && assetData[priceTimekey];

    let trend: TrendType = 'neutral';

    if (priceChange) {
      const priceChangeValue = Number(priceChange.value);

      trend =
        priceChangeValue > 0
          ? 'positive'
          : priceChangeValue < 0
            ? 'negative'
            : 'neutral';
    }

    return {
      priceChange,
      trend,
    };
  }, [assetData, timeRange]);

  return (
    <AssetDetailsDataContext.Provider
      value={{
        assetData,
        assetHistory,
        loading,
        timeRange,
        changeTimeRange: changeTimeRange,
        trend,
        priceChange,
      }}
    >
      {children}
    </AssetDetailsDataContext.Provider>
  );
};
